import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X, Zap, ArrowRight } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { api } from '../services/api'

const FREE_FEATURES = [
  { text: '1 material activo a la vez',           included: true  },
  { text: 'PDFs hasta 20 páginas',                included: true  },
  { text: '1 quiz, resumen y mapa por material',  included: true  },
  { text: 'Historial de 7 días',                  included: true  },
  { text: 'Regenerar quizzes',                    included: false },
  { text: '3 niveles de dificultad',              included: false },
  { text: 'Explicaciones con citas de fuente',    included: false },
  { text: 'Progreso comparativo entre quizzes',   included: false },
  { text: 'Dashboard para padres',                included: false },
]

const PRO_FEATURES = [
  { text: 'Materiales ilimitados (100 pág, 25MB)', included: true },
  { text: 'Regenerar quizzes sin límite',           included: true },
  { text: '3 niveles de dificultad (Fácil/Medio/Difícil)', included: true },
  { text: '3 niveles de detalle en resúmenes',      included: true },
  { text: 'Explicaciones con citas del material',   included: true },
  { text: 'Progreso comparativo entre intentos',    included: true },
  { text: 'Dashboard para padres (hasta 5 hijos)',  included: true },
  { text: 'Historial permanente',                   included: true },
  { text: 'Badges y logros',                        included: true },
  { text: 'Exportar resúmenes a PDF',               included: true },
]

const STRIPE_PRICE_PRO = import.meta.env.VITE_STRIPE_PRICE_MONTHLY || 'price_1TJfxlEH9OGCTDAu7LYSXkmU'

export default function PricingPage() {
  const [loading, setLoading] = useState(false)
  const { user, isAuthenticated } = useAuthStore()

  const handleUpgrade = async () => {
    if (!isAuthenticated) return
    setLoading(true)
    try {
      const { url } = await api.createCheckoutSession(STRIPE_PRICE_PRO)
      window.location.href = url
    } catch {
      alert('No se pudo iniciar el proceso de pago. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(79,70,229,0.1) 0%, transparent 65%)' }}
      />

      {/* Top nav */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-5 max-w-6xl mx-auto">
        <Link to="/" className="font-bold text-xl" style={{ color: '#F1F5F9' }}>kapri</Link>
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <Link to="/" className="btn-secondary text-sm">Ir al dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="btn-secondary text-sm">Iniciar sesión</Link>
              <Link to="/register" className="btn-primary text-sm">Registrarse</Link>
            </>
          )}
        </div>
      </nav>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <span
            className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4"
            style={{ background: 'rgba(79,70,229,0.15)', color: '#818CF8', border: '1px solid rgba(99,102,241,0.3)' }}
          >
            Planes simples, sin sorpresas
          </span>
          <h1 className="text-4xl font-bold mb-3" style={{ color: '#F1F5F9' }}>
            Invertí en tu aprendizaje
          </h1>
          <p className="text-lg max-w-xl mx-auto" style={{ color: '#94A3B8' }}>
            Accede a todas las herramientas de IA que necesitas para dominar cualquier materia
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free */}
          <div className="glass-card p-7">
            <span
              className="text-xs font-semibold px-2.5 py-1 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.07)', color: '#94A3B8' }}
            >
              Free
            </span>
            <div className="mt-4 mb-6">
              <span className="text-5xl font-bold" style={{ color: '#F1F5F9' }}>$0</span>
              <span className="text-sm ml-1" style={{ color: '#64748B' }}>/mes</span>
              <p className="text-sm mt-1" style={{ color: '#64748B' }}>Para empezar a explorar</p>
            </div>
            <ul className="space-y-3 mb-7">
              {FREE_FEATURES.map((f) => (
                <li key={f.text} className="flex items-start gap-2.5 text-sm">
                  {f.included
                    ? <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#10B981' }} />
                    : <X    className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#475569' }} />
                  }
                  <span style={{ color: f.included ? '#CBD5E1' : '#475569' }}>{f.text}</span>
                </li>
              ))}
            </ul>
            <Link to={isAuthenticated ? '/' : '/register'} className="btn-secondary w-full flex items-center justify-center">
              {isAuthenticated && user?.plan === 'free' ? 'Plan actual' : 'Empezar gratis'}
            </Link>
          </div>

          {/* Pro */}
          <div
            className="relative rounded-2xl p-7"
            style={{
              background: 'rgba(79,70,229,0.08)',
              border: '1px solid rgba(99,102,241,0.4)',
              boxShadow: '0 0 40px rgba(79,70,229,0.15)',
            }}
          >
            <div className="flex items-center justify-between mb-1">
              <span
                className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)', color: 'white' }}
              >
                Pro
              </span>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(251,191,36,0.15)', color: '#FBBF24' }}
              >
                Más popular
              </span>
            </div>
            <div className="mt-3 mb-6">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl line-through" style={{ color: '#64748B' }}>$24.99</span>
                <span
                  className="text-xs font-bold px-1.5 py-0.5 rounded-md"
                  style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}
                >
                  -40%
                </span>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-5xl font-bold" style={{ color: '#F1F5F9' }}>$14.95</span>
                <span className="text-sm mb-1.5" style={{ color: '#64748B' }}>/mes</span>
              </div>
              <p className="text-sm mt-1" style={{ color: '#64748B' }}>
                El plan de los estudiantes que quieren destacar
              </p>
            </div>
            <ul className="space-y-3 mb-7">
              {PRO_FEATURES.map((f) => (
                <li key={f.text} className="flex items-start gap-2.5 text-sm">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#10B981' }} />
                  <span style={{ color: '#CBD5E1' }}>{f.text}</span>
                </li>
              ))}
            </ul>

            {user?.plan === 'pro' ? (
              <div
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)' }}
              >
                <Check className="w-4 h-4" /> Plan actual
              </div>
            ) : (
              <button
                onClick={isAuthenticated ? handleUpgrade : undefined}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ boxShadow: '0 0 20px rgba(79,70,229,0.4)' }}
              >
                {loading ? 'Redirigiendo...' : (
                  <>
                    <Zap className="w-4 h-4" />
                    {isAuthenticated ? 'Empezar con Pro' : 'Registrarse y empezar'}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
            <p className="text-xs text-center mt-3" style={{ color: '#475569' }}>
              Cancelá cuando quieras. Sin permanencia.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mt-16">
          <h2 className="text-xl font-semibold text-center mb-8" style={{ color: '#E2E8F0' }}>
            Preguntas frecuentes
          </h2>
          {[
            { q: '¿Puedo cambiar de plan en cualquier momento?', a: 'Sí. Puedes cancelar o cambiar tu suscripción desde el panel de facturación en cualquier momento.' },
            { q: '¿Qué formatos de archivo acepta Kapri?', a: 'PDF, DOCX y TXT. El plan Free acepta hasta 20 páginas; el Pro hasta 100 páginas y 25MB.' },
            { q: '¿Los datos de mis materiales son privados?', a: 'Sí. Tus materiales solo son accesibles para ti y se almacenan de forma segura en AWS.' },
            { q: '¿El plan Pro funciona para padres también?', a: 'Sí. Con Pro puedes vincular hasta 5 hijos y monitorear su progreso desde el dashboard de padres.' },
          ].map(({ q, a }) => (
            <details
              key={q}
              className="group mb-3 rounded-xl overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <summary
                className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-sm"
                style={{ color: '#E2E8F0', listStyle: 'none' }}
              >
                {q}
                <span className="ml-4 flex-shrink-0 text-lg" style={{ color: '#475569' }}>+</span>
              </summary>
              <div className="px-5 pb-4 text-sm" style={{ color: '#94A3B8' }}>{a}</div>
            </details>
          ))}
        </div>
      </div>
    </div>
  )
}
